<script lang="ts" setup>import { computed as _computed } from 'vue';

import { AppButton, ScrollbarTiny } from '@pohlcon/design-system';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { type ISOPRO, type WallStructure } from '~/models';
import { useProductsStore, useWallStructuresStore, useMainStore } from '~/store';

import ProductSelector from './Product/ProductSelector.vue';
import WallStructureSelector from './WallStructure/WallStructureSelector.vue';
import { LegalFooter } from '~/components';

const { t } = useI18n();
const wallStructureStore = useWallStructuresStore();
const productsStore = useProductsStore();
const mainStore = useMainStore();
const router = useRouter();

const selectedWallStructure = _computed({
  get () {
    return wallStructureStore.selectedWallStructure;
  },
  set (ws: WallStructure | null) {
    wallStructureStore.selectedWallStructure = ws;
  }
});

const selectedProduct = _computed({
  get: () => {
    return productsStore.selectedProduct as ISOPRO | null;
  },
  set: (product: ISOPRO | null) => {
    productsStore.selectedProduct = product;
  }
});

const selectedThickness: number = _computed(() =>
  wallStructureStore.selectedThickness);

const validateSelection: boolean = _computed(() => {
  const validThickness = !selectedWallStructure.value?.thicknesses || !!selectedThickness.value;
  return !!selectedProduct.value && !!selectedWallStructure.value && validThickness;
});

const showProductSelection: boolean = _computed(() =>
  !!selectedWallStructure.value && (!selectedWallStructure.value.hasThicknesses || !!selectedThickness.value));

const buttonTitle: string = _computed(() =>
  `selectView.${validateSelection.value ? 'next' : 'deactivated'}.title`);

const handleNextStep = () => {
  mainStore.completeStep(true);
  mainStore.setNextStep();
  router.push(mainStore.currentStep.to);
};

const handleBack = async () => {
  await mainStore.setPreviousStep();
  router.push('/');
};
</script>

<template>
  <!-- TODO: collapse/expand sections when (not) in use -->
  <div id="select-view">
    <ScrollbarTiny>
      <div class="selection-wrapper">
        <WallStructureSelector v-model="selectedWallStructure" />
        <div
          v-if="showProductSelection"
          class="product-selection-wrapper"
        >
          <span
            class="sub-header"
            v-html="t('selectView.productSelect.subtitle')"
          />
          <ProductSelector v-model="selectedProduct" />
        </div>
      </div>

      <div class="button-wrapper">
        <AppButton
          icon="chevron_left"
          :title="t('selectView.back.title')"
          :text="t('actions.back')"
          :hollow="true"
          secondary
          @click="handleBack"
          cypress-id="select-view-back-button"
        />
        <AppButton
          icon="chevron_right"
          :title="t(buttonTitle)"
          :disabled="!validateSelection"
          :text="t('actions.next')"
          :trailingIcon="true"
          secondary
          @click="handleNextStep"
          cypress-id="select-view-next-button"
        />
      </div>
    </ScrollbarTiny>
  </div>
  <LegalFooter />
</template>


<style lang="scss" scoped>
@use '../../variables' as *;

#select-view {
  display: grid;
  max-height: 75vh;
  grid-template-rows: max-content 1fr max-content;

  @media screen and (max-width: $breakpoint-tablet) {
    max-height: 78vh;
  }
}

.selection-wrapper {
  overflow-y: auto;
  max-height: 69vh;
  padding-right: var(--gutter-2xs);
  padding-right: var(--gutter-2xs);

  @media screen and (max-width: $breakpoint-tablet) {
    max-height: 72vh;
  }
}

.product-selection-wrapper {
  display: grid;
  grid-template-rows: max-content max-content max-content 1fr;
  padding: var(--gutter-2xs);
}

.button-wrapper {
  display: grid;
  margin-top: 1rem;
  justify-content: end;
  grid-auto-flow: column;
  column-gap: 10px;
}
</style>
